import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import ApiClient from '../utils/ApiClient';
import {
    formatGain, describeAge, basketUnion, basketUnits, buildFcMatrix, buildFcMapPins,
    displayVariant, planSnapshot,
} from '../utils/placementBasket';
import PrintLabelsAction from '../components/Common/PrintLabelsAction';
import FcPinMap from '../components/Common/FcPinMap';

/**
 * Placement combination workspace — /shipment-plans?asin=…
 *
 * Spec: docs/specs/placement-combination-workspace.md
 *
 * The planner picks ONE best plan per ASIN, which is the wrong question. Amazon returns a 5-FC
 * split every time, so units buy DEPTH and only combining plans buys REACH. Owner: "if we have 15
 * Ditra rolls it could be 3 shipment plans, of five units, each unit going to a separate FC, for a
 * total of 15 FCs".
 *
 * Ranking is by MARGINAL GVS gain — what a draw adds on top of the basket — not absolute score.
 * On B018A2XNC6 the FC GEG2 appears in 20 of 68 open draws; absolute ranking would stack it three
 * times and add almost nothing.
 */

function FcChip({ code, isNew, covered }) {
    // New FCs carry the value; already-covered ones are shown but recede, so overlap is VISIBLE
    // rather than inferred. This is the whole reason the matrix exists.
    const cls = isNew
        ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
        : covered
            ? 'bg-gray-100 text-gray-400 border-gray-200 line-through'
            : 'bg-gray-100 text-gray-600 border-gray-200';
    return (
        <span className={`inline-block px-1.5 py-0.5 mr-1 mb-1 text-xs font-mono rounded border ${cls}`}
              title={isNew ? 'new — not yet covered' : 'already covered by your current stock or basket'}>
            {code}
        </span>
    );
}

/**
 * FC x plan matrix — one row per candidate plan, one column per FC, ordered so the FCs every plan
 * shares form a block on the left and the differentiating tail sits on the right.
 *
 * A picked row's cells turn solid; the columns it covers gain a check in the "in basket" row, so
 * adding a second plan that lands on the same columns is visibly wasteful rather than something
 * the operator has to work out from two lists of chips.
 */
function FcPlanMatrix({ matrix, currentFcs, selected, onToggle }) {
    const picked = new Set(selected || []);
    const stocked = new Set(currentFcs || []);
    const inBasket = new Set(stocked);
    matrix.rows.forEach(r => {
        if (r.variants.some(v => picked.has(v.runId))) r.fcs.forEach(fc => inBasket.add(fc));
    });

    if (!matrix.rows.length) return null;

    return (
        <div className="mb-4 border border-gray-200 rounded-md overflow-x-auto">
            <table className="text-xs border-collapse">
                <thead>
                    <tr className="bg-gray-50">
                        <th className="sticky left-0 bg-gray-50 px-3 py-2 text-left font-medium text-gray-600 border-r">
                            Plan
                        </th>
                        {matrix.columns.map(c => (
                            <th key={c.fc}
                                title={`${c.fc} — in ${c.plans} plan${c.plans !== 1 ? 's' : ''}`
                                    + (c.alreadyStocked ? ' · already stocked' : '')}
                                className={`px-1 py-2 font-mono font-normal border-r last:border-r-0 ${
                                    c.alreadyStocked ? 'text-gray-400' : 'text-gray-700'}`}>
                                {c.fc}
                            </th>
                        ))}
                        <th className="px-2 py-2 text-right font-medium text-gray-600">Adds</th>
                    </tr>
                    {/* What the basket already covers. Stocked FCs show their unit DEPTH (owner,
                        2026-08-26: "instead of a dot, this should show the number of units"); FCs
                        covered only by a picked plan keep the check. */}
                    <tr className="bg-emerald-50 border-b-2 border-emerald-200">
                        <th className="sticky left-0 bg-emerald-50 px-3 py-1 text-left font-medium text-emerald-800 border-r whitespace-nowrap">
                            In basket
                        </th>
                        {matrix.columns.map(c => (
                            <td key={c.fc} className="px-1 py-1 text-center border-r last:border-r-0">
                                {c.alreadyStocked
                                    ? <span className="font-medium text-emerald-800"
                                            title={c.stockUnits != null
                                                ? `${c.stockUnits} unit${c.stockUnits !== 1 ? 's' : ''} currently at ${c.fc}`
                                                : `${c.fc} holds stock — unit count unavailable`}>
                                        {c.stockUnits != null ? c.stockUnits : '✓'}
                                      </span>
                                    : inBasket.has(c.fc)
                                        ? <span className="text-emerald-600">✓</span>
                                        : <span className="text-gray-200">·</span>}
                            </td>
                        ))}
                        <td />
                    </tr>
                    {/* Owner, 2026-08-26: "so we understand where other shipments are happening" --
                        units on confirmed shipments still in transit to each FC. Awareness only:
                        never counted as coverage in the ranking, and an FC touched only by an
                        in-transit shipment still gets a column (plans = 0, sorted last). */}
                    {matrix.columns.some(c => c.inboundUnits != null) && (
                        <tr className="bg-sky-50 border-b-2 border-sky-200">
                            <th className="sticky left-0 bg-sky-50 px-3 py-1 text-left font-medium text-sky-800 border-r whitespace-nowrap"
                                title="Units on confirmed shipments still in transit (45-day window)">
                                Inbound
                            </th>
                            {matrix.columns.map(c => (
                                <td key={c.fc} className="px-1 py-1 text-center border-r last:border-r-0">
                                    {c.inboundUnits != null
                                        ? <span className="font-medium text-sky-800"
                                                title={`${c.inboundUnits} unit${c.inboundUnits !== 1 ? 's' : ''} on confirmed shipments still in transit to ${c.fc}`}>
                                            {c.inboundUnits}
                                          </span>
                                        : <span className="text-gray-200">·</span>}
                                </td>
                            ))}
                            <td />
                        </tr>
                    )}
                </thead>
                <tbody>
                    {matrix.rows.map(r => {
                        const rowPicked = r.variants.some(v => picked.has(v.runId));
                        // The variant this row stands for: the picked one when in the basket, else
                        // the cheapest live one. The checkbox, the label and the unit cells all
                        // read the SAME variant, so selecting toggles exactly what the cells show.
                        const shown = displayVariant(r.variants, selected);
                        return (
                            <tr key={r.key}
                                className={`border-t ${rowPicked ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}>
                                <th className={`sticky left-0 px-3 py-1 text-left font-normal border-r whitespace-nowrap ${
                                        rowPicked ? 'bg-emerald-50' : 'bg-white'}`}>
                                    <label className={`inline-flex items-center gap-1.5 ${
                                            shown ? 'cursor-pointer' : 'cursor-default'}`}
                                           title={shown ? `run ${shown.runId} · ${shown.units} units` : 'no confirmable variant'}>
                                        <input
                                            type="checkbox"
                                            checked={rowPicked}
                                            disabled={!shown}
                                            onChange={() => shown && onToggle(shown.runId)}
                                            className="h-3 w-3 rounded border-gray-300 text-emerald-600"
                                        />
                                        <span className={shown ? 'text-indigo-600' : 'text-gray-300'}>
                                            {shown ? `${shown.units} units` : '—'}
                                        </span>
                                        {r.recommended && (
                                            <span className="px-1 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-700 border border-emerald-300"
                                                  title="Scored TAKE — recommended to confirm">
                                                TAKE
                                            </span>
                                        )}
                                    </label>
                                </th>
                                {matrix.columns.map(c => {
                                    const covers = r.fcs.has(c.fc);
                                    // Three states, not two: a cell this plan covers but the basket
                                    // ALREADY has is the overlap the page is built to expose. The
                                    // cell shows the UNITS the plan sends there; a draw stored
                                    // without a per-shipment item split falls back to the mark.
                                    const dup = covers && inBasket.has(c.fc) && !rowPicked;
                                    const units = shown && shown.fcUnits ? shown.fcUnits[c.fc] : undefined;
                                    return (
                                        <td key={c.fc} className="px-1 py-1 text-center border-r last:border-r-0">
                                            {!covers ? <span className="text-gray-200">·</span>
                                                : dup ? <span className="text-amber-500" title="already covered — adds nothing">
                                                        {units != null ? units : '◦'}
                                                    </span>
                                                : <span className={rowPicked ? 'font-semibold text-emerald-700' : 'text-indigo-600'}>
                                                        {units != null ? units : '●'}
                                                    </span>}
                                        </td>
                                    );
                                })}
                                <td className={`px-2 py-1 text-right font-medium whitespace-nowrap ${
                                        (r.marginalGain1Day || 0) > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>
                                    {formatGain(r.marginalGain1Day)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <div className="px-3 py-2 text-xs text-gray-500 border-t bg-gray-50">
                cell numbers are the units the plan sends to that FC (<span className="text-indigo-600">●</span> when
                the draw has no per-FC split) · <span className="text-amber-500">amber</span> = the basket already
                covers it · the In-basket row shows units currently held at stocked FCs · tick rows to select —
                selected plans appear above with Confirm and label printing
            </div>
        </div>
    );
}

// The FC map card now lives in components/Common/FcPinMap.js, shared with the Shipment
// Queue's hover popover (owner, 2026-09-02) so the two surfaces can never drift.

export default function PlacementWorkspace() {
    const [params, setParams] = useSearchParams();
    const asin = params.get('asin') || '';
    // Owner, 2026-09-02: composition mode — "when the shipping plans is multiple skus, the
    // placement plan page should be looking at just those skus". ?asins=B1,B2 scopes the page to
    // the shipping plan's whole SKU set; the server matches draws of exactly that composition
    // and blends/sums the figures across the products.
    const asinsCsv = params.get('asins') || '';
    const compositionAsins = useMemo(
        () => asinsCsv.split(',').map(s => s.trim()).filter(Boolean),
        [asinsCsv]);
    const isComposition = compositionAsins.length > 1;
    const fromPo = params.get('fromPo') || '';
    const [asinInput, setAsinInput] = useState(isComposition ? asinsCsv : asin);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState([]);
    const [showExpired, setShowExpired] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [showMatrix, setShowMatrix] = useState(false);
    const [unitBudget, setUnitBudget] = useState('');
    // Snapshots of the selected plans, captured at pick time (runId -> planSnapshot). A confirmed
    // run leaves the open-draw pool on the next reload, so its row here must not depend on it.
    const [planRows, setPlanRows] = useState({});
    // runId -> {shipmentCount}: the Confirm button one-way latch. Confirm is irreversible at
    // Amazon, so the latch never resets short of leaving the page.
    const [confirmedPlans, setConfirmedPlans] = useState({});
    const [confirmingId, setConfirmingId] = useState(null);
    const [removing, setRemoving] = useState(false);
    const [confirmErrors, setConfirmErrors] = useState({});
    // Print buttons stay ACTIVE after use (reprints happen) — they only change colour.
    const [printedIds, setPrintedIds] = useState({});
    // A re-rank in flight: the page stays interactive, this only drives the small hint.
    const [reranking, setReranking] = useState(false);
    const loadSeqRef = useRef(0);

    // Owner, 2026-08-26: "when a FC checkbox is selected, the page stops and loads for several
    // seconds" — a re-rank is a BACKGROUND refresh. Only the first load (nothing on screen yet)
    // may blank the page; while a re-rank is in flight the operator keeps selecting, and the
    // sequence counter makes the answers last-write-wins so a stale response from a rapid earlier
    // click can never overwrite a newer basket's ranking.
    const load = useCallback(async (basket, background = false) => {
        if (!asin && !isComposition) { setData(null); return; }
        const seq = ++loadSeqRef.current;
        if (background) setReranking(true); else setLoading(true);
        setError(null);
        try {
            const basketQ = basket && basket.length ? `basket=${basket.join(',')}` : '';
            const url = isComposition
                ? `/api/shipment-planner/placement-candidates?asins=${encodeURIComponent(compositionAsins.join(','))}`
                    + (basketQ ? `&${basketQ}` : '')
                : `/api/shipment-planner/placement-candidates/${encodeURIComponent(asin)}`
                    + (basketQ ? `?${basketQ}` : '');
            const res = await ApiClient.get(url, { timeout: 120000 });
            if (seq === loadSeqRef.current) setData(res.data || null);
        } catch (err) {
            if (seq === loadSeqRef.current) {
                setError(err.response?.data?.error || err.message);
                // A failed re-rank keeps the page: stale rankings beat a blank screen.
                if (!background) setData(null);
            }
        }
        if (seq === loadSeqRef.current) { setLoading(false); setReranking(false); }
    }, [asin, isComposition, compositionAsins]);

    useEffect(() => {
        setAsinInput(isComposition ? asinsCsv : asin);
        setSuggestions([]);
        setPlanRows({});
        setConfirmedPlans({});
        setConfirmErrors({});
        setPrintedIds({});
        load([]);
    }, [asin, asinsCsv, isComposition, load]);

    // Suggestions are budget-driven, so they are fetched on demand rather than with the page: the
    // operator has to say how many units are actually available before "take these 3 plans" means
    // anything.
    const loadSuggestions = async (e) => {
        e.preventDefault();
        if (!asin) return;
        const units = parseInt(unitBudget, 10);
        try {
            const res = await ApiClient.get(
                `/api/shipment-planner/placement-suggestions/${encodeURIComponent(asin)}`
                + (units > 0 ? `?units=${units}` : ''), { timeout: 180000 });
            setSuggestions(res.data?.suggestions || []);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        }
    };

    // Selecting captures a snapshot of the plan (ids, units, FCs) at pick time — the selected-plans
    // section renders from these so a row survives the reload that follows its own confirm.
    const snapshotPlans = useCallback((runIds, groups) => {
        setPlanRows(prev => {
            const next = {};
            runIds.forEach(id => {
                next[id] = prev[id] || planSnapshot(groups, id);
            });
            return next;
        });
    }, []);

    // Applying a suggestion just seeds the basket — the operator can then add or drop plans and
    // watch the union move. A suggestion is a starting point, not a decision.
    const applySuggestion = async (s) => {
        const ids = s.runIds || [];
        setSelected(ids);
        snapshotPlans(ids, data?.groups);
        await load(ids, true);
    };

    // Re-rank on every basket change: marginal gain is meaningless if it isn't recomputed against
    // what has already been picked.
    const toggle = async (runId) => {
        const next = selected.includes(runId)
            ? selected.filter(r => r !== runId)
            : [...selected, runId];
        setSelected(next);
        snapshotPlans(next, data?.groups);
        await load(next, true);
    };

    // The single-plan confirm — the same guarded endpoint the planner page uses (naming pass,
    // used-condition guard, freight-quote check all live server-side). One plan per call, never a
    // bulk fan-out: runs 211/220/222 are why.
    const confirmPlan = async (p) => {
        if (!p?.inboundPlanId || !p?.placementOptionId) return;
        if (!window.confirm(
            `Confirm run ${p.runId} — ${p.units} units to ${(p.fcs || []).length} FCs — with Amazon?\n\n`
            + 'This locks the placement, books Amazon partnered transport, and cannot be undone.'
        )) return;
        setConfirmingId(p.runId);
        setConfirmErrors(prev => ({ ...prev, [p.runId]: null }));
        try {
            const res = await ApiClient.post('/api/shipment-planner/confirm', {
                planId: p.inboundPlanId,
                placementOptionId: p.placementOptionId,
                acknowledgeUsedCondition: false,
            }, { timeout: 120000 });
            // The whole snapshot rides along: planRows is rebuilt from the live selection on every
            // toggle, so a confirmed row must carry its own copy to keep rendering afterwards.
            setConfirmedPlans(prev => ({
                ...prev, [p.runId]: { ...p, shipmentCount: res.data?.shipmentCount ?? null },
            }));
            // Reload WITHOUT the confirmed run: it has left the open pool, and the remaining
            // basket's marginal gains must be recomputed against a world where it is stock-bound.
            const next = selected.filter(r => r !== p.runId);
            setSelected(next);
            await load(next, true);
        } catch (err) {
            setConfirmErrors(prev => ({
                ...prev,
                [p.runId]: err.response?.data?.error || err.message ||
                    'Confirm failed — the plan was NOT sent to Amazon.',
            }));
        }
        setConfirmingId(null);
    };

    // Owner, 2026-08-26: "cancel/remove plans by selecting the check mark and pressing remove
    // plan - this should stop any continuous plans runs". The opposite of confirming: cancels the
    // checked draws server-side AND stops this ASIN's background watch — without the second half
    // the 6-hourly cycle would re-create what was just removed.
    const removePlans = async () => {
        const ids = selected.filter(id => !confirmedPlans[id]);
        if (!ids.length) return;
        if (!window.confirm(
            `Remove ${ids.length} plan${ids.length !== 1 ? 's' : ''}?\n\n`
            + 'This cancels the selected draws and stops the background watch for this ASIN, '
            + 'so plans stop being drawn automatically. Confirmed shipments are never touched.'
        )) return;
        setRemoving(true);
        try {
            // Composition mode posts every product of the shipping plan so ALL their watches stop.
            await ApiClient.post('/api/shipment-planner/placement-plans/remove',
                isComposition ? { asins: compositionAsins, runIds: ids } : { asin, runIds: ids });
            setSelected([]);
            snapshotPlans([], null);
            await load([], true);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        }
        setRemoving(false);
    };

    const groups = data?.groups || [];
    const live = groups.filter(g => g.anyLive);
    const expired = groups.filter(g => !g.anyLive);
    const union = useMemo(() => basketUnion(data?.currentFcs, groups, selected), [data, groups, selected]);
    const units = useMemo(() => basketUnits(groups, selected), [groups, selected]);
    const addedFcs = union.length - (data?.currentFcs?.length || 0);
    const matrix = useMemo(
        () => buildFcMatrix(data?.currentFcs, live, data?.currentFcUnits, data?.inboundFcUnits),
        [data, live]);

    // What the selected-plans section renders: confirmed rows first (they carry their own
    // snapshot), then the still-open selection.
    const sectionRows = useMemo(() => {
        const confirmed = Object.values(confirmedPlans);
        const open = selected
            .filter(id => !confirmedPlans[id])
            .map(id => planRows[id])
            .filter(Boolean);
        return [...confirmed, ...open];
    }, [confirmedPlans, selected, planRows]);

    // The FC dictionary for the map (one small payload, fetched once — same contract as the
    // Shipment Queue's tooltip fetch). An empty dictionary degrades to "location unknown".
    const [fcLocations, setFcLocations] = useState({});
    useEffect(() => {
        ApiClient.get('/api/shipment-planner/fc-locations')
            .then(res => setFcLocations(res.data || {}))
            .catch(() => { /* the map lists every FC as location unknown instead */ });
    }, []);

    // The map's three layers: current stock, confirmed inbound, and the FCs of the selected
    // plans (from the same snapshots the selected-plans rows render, so map and rows agree).
    const selectedPlanFcs = useMemo(
        () => [...new Set(sectionRows.flatMap(p => p.fcs || []))],
        [sectionRows]);
    const fcMapData = useMemo(
        () => buildFcMapPins(fcLocations, data?.currentFcUnits, data?.inboundFcUnits, selectedPlanFcs),
        [fcLocations, data, selectedPlanFcs]);

    const submitAsin = (e) => {
        e.preventDefault();
        setSelected([]);
        // Keep the PO back-reference through an ASIN change: the operator is usually walking the
        // lines of the same PO, and dropping it would strand them without their way back.
        // A comma-separated entry is a composition (owner 2026-09-02) — same as arriving via the
        // queue's mixed-row link.
        const t = asinInput.trim();
        setParams({
            ...(fromPo ? { fromPo } : {}),
            ...(t ? (t.includes(',') ? { asins: t } : { asin: t }) : {}),
        });
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Owner, 2026-08-26: "a back button to take you back to the open lines". The page
                opens in a new tab, so browser-back has nowhere to go — this deep-links to the
                planner's ?po=, which reopens the PO panel on that PO's lines. */}
            {fromPo && (
                <Link to={`/shipment-planner?mode=asin&po=${encodeURIComponent(fromPo)}`}
                      className="inline-block mb-2 text-sm text-indigo-600 hover:text-indigo-800">
                    ← Back to PO {fromPo} open lines
                </Link>
            )}
            <div className="flex items-baseline justify-between mb-4">
                <h1 className="text-2xl font-semibold text-gray-900">Placement plans</h1>
                <Link to="/shipment-runs" className="text-sm text-indigo-600 hover:text-indigo-800">
                    Shipment queue →
                </Link>
            </div>

            <form onSubmit={submitAsin} className="mb-5 flex gap-2">
                <input
                    value={asinInput}
                    onChange={e => setAsinInput(e.target.value)}
                    placeholder="ASIN, e.g. B018A2XNC6"
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm font-mono w-64"
                />
                <button type="submit"
                        className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700">
                    Show plans
                </button>
            </form>

            {loading && <div className="text-sm text-gray-500">Loading draws and projecting GVS…</div>}
            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}

            {data && !loading && (
                <>
                    {/* Owner, 2026-09-02: composition mode banner — the page is scoped to the
                        shipping plan's whole SKU set, and every figure below spans them. */}
                    {isComposition && (
                        <div className="mb-3 rounded-md border border-violet-200 bg-violet-50 px-4 py-2">
                            <span className="text-sm font-semibold text-violet-900">
                                Shipping plan · {compositionAsins.length} products
                            </span>
                            <span className="ml-2">
                                {compositionAsins.map(a => (
                                    <span key={a} className="inline-block px-1.5 py-0.5 mr-1 text-xs font-mono rounded border bg-white border-violet-200 text-violet-800">
                                        {a}
                                    </span>
                                ))}
                            </span>
                            <span className="ml-1 text-xs text-violet-700">
                                stock and inbound are summed, GVS is blended (page-view weighted) across these products —
                                only draws of exactly this composition appear below
                            </span>
                        </div>
                    )}
                    {/* Where we stand. GVS <=1 day is the headline: Amazon's threshold is same-day
                        or next-day, and a 2-day promise earns nothing on it. */}
                    <div className="mb-5 rounded-md border border-indigo-100 bg-indigo-50 px-4 py-3">
                        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                            <span className="text-sm text-gray-600">Today</span>
                            <span className="text-2xl font-semibold text-indigo-900">
                                {data.currentGvs1Day}% <span className="text-sm font-normal text-gray-600">GVS ≤1 day</span>
                            </span>
                            <span className="text-sm text-gray-600">≤2 day {data.currentGvs2Day}%</span>
                            <span className="text-sm text-gray-600">
                                {(data.currentFcs || []).length} FC{(data.currentFcs || []).length !== 1 ? 's' : ''} holding stock
                            </span>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                            {data.openDraws} open draw{data.openDraws !== 1 ? 's' : ''} ·{' '}
                            <span className="font-medium text-gray-700">{data.liveDraws} still confirmable</span>
                            {reranking && (
                                <span className="ml-2 text-xs text-indigo-500 animate-pulse">
                                    re-ranking against your selection…
                                </span>
                            )}
                            {data.expiredDraws > 0 && ` · ${data.expiredDraws} expired`} ·{' '}
                            {(data.unionFcs || []).length} distinct FCs available across all draws
                            <span className="ml-1 cursor-help underline decoration-dotted"
                                  title={'GVS is projected from the UPS transit matrix and the demand map, not measured. '
                                      + 'The metro split inside an ASIN comes from ORDERS, which are censored where we are '
                                      + 'slow, so a projection reads slightly optimistic. Amazon reports the real figure '
                                      + 'per ASIN but exposes no API for it.'}>
                                how is this calculated?
                            </span>
                        </div>
                    </div>

                    {/* Suggested combinations. Reach vs depth is the real trade, so both are shown
                        with their cost in units — never one "recommended" answer.
                        Single mode only: suggest() is per-ASIN set-cover; a composition's draws
                        are already the shipping-plan-level choices the owner decides between. */}
                    {!isComposition && (
                    <div className="mb-5">
                        <form onSubmit={loadSuggestions} className="flex items-center gap-2 mb-2">
                            <h2 className="text-sm font-semibold text-gray-700">Suggested combinations</h2>
                            <span className="text-sm text-gray-500">for</span>
                            <input
                                value={unitBudget}
                                onChange={e => setUnitBudget(e.target.value)}
                                placeholder="units"
                                className="border border-gray-300 rounded-md px-2 py-1 text-sm w-24"
                            />
                            <span className="text-sm text-gray-500">units on hand</span>
                            <button type="submit"
                                    className="px-3 py-1 text-sm rounded-md border border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                                Compute
                            </button>
                        </form>

                        {suggestions.length > 0 && (
                            <div className="grid gap-3 md:grid-cols-2">
                                {suggestions.map(s => (
                                    <div key={s.key} className="border border-gray-200 rounded-md px-4 py-3">
                                        <div className="flex items-baseline justify-between gap-3">
                                            <span className="font-semibold text-gray-900">{s.label}</span>
                                            <span className="text-lg font-semibold text-emerald-700">
                                                {formatGain(s.gvs1DayGain)}
                                            </span>
                                        </div>
                                        <div className="text-sm text-gray-700 mt-1">
                                            {(s.runIds || []).length} plan{(s.runIds || []).length !== 1 ? 's' : ''}
                                            {' · '}{s.units} units
                                            {' · '}{(s.fcs || []).length} FCs
                                            {' → '}<span className="font-medium">{s.gvs1DayAfter}% ≤1 day</span>
                                        </div>
                                        <div className="mt-2">{(s.fcs || []).map(fc => (
                                            <FcChip key={fc} code={fc} isNew={!(data.currentFcs || []).includes(fc)} />
                                        ))}</div>
                                        {/* Double-covered FCs are stated, not hidden: the gain figure already
                                            discounts them, and the operator should see what they paid twice for. */}
                                        {(s.overlaps || []).length > 0 && (
                                            <div className="mt-1 text-xs text-amber-700">
                                                Overlaps — covered more than once: {(s.overlaps || []).join(', ')}
                                            </div>
                                        )}
                                        <div className="mt-1 text-xs text-gray-500">{s.rationale}</div>
                                        <button onClick={() => applySuggestion(s)}
                                                className="mt-2 px-3 py-1 text-xs rounded-md bg-indigo-600 text-white hover:bg-indigo-700">
                                            Load into basket
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    )}

                    <div className="flex items-baseline justify-between mb-2">
                        <h2 className="text-sm font-semibold text-gray-700">
                            Confirmable draws — ranked by what each ADDS
                        </h2>
                        <button onClick={() => setShowMatrix(v => !v)}
                                className="text-sm text-indigo-600 hover:text-indigo-800 underline">
                            {showMatrix ? 'Hide' : 'Show'} FC × plan matrix
                        </button>
                    </div>

                    {/* The matrix. Chips per plan show WHAT each plan covers; only a matrix shows
                        where plans OVERLAP, which is the thing that decides a combination. */}
                    {showMatrix && <FcPlanMatrix
                        matrix={matrix}
                        currentFcs={data.currentFcs || []}
                        selected={selected}
                        onToggle={toggle}
                    />}

                    {/* Owner, 2026-08-26 (second pass): the map + selected plans sit UNDER the FC
                        grid, map still directly above the selected-plans rows — current stock,
                        inbound, and the selection, on one picture of the country. */}
                    {sectionRows.length > 0 && (fcMapData.pins.length > 0 || fcMapData.unknown.length > 0) && (
                        <div className="mb-5 shadow rounded-md border border-gray-200">
                            <FcPinMap pins={fcMapData.pins} unknown={fcMapData.unknown} />
                        </div>
                    )}

                    {/* Selected plans: live union up top, then one row per plan with the confirm
                        and label actions. Confirm is one plan per click through the guarded
                        single-plan endpoint — there is deliberately no confirm-all. */}
                    {sectionRows.length > 0 && (
                        <div className="mb-5 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3">
                            <div className="flex flex-wrap items-baseline gap-x-6">
                                <span className="font-semibold text-emerald-900">
                                    {sectionRows.length} plan{sectionRows.length !== 1 ? 's' : ''} selected
                                </span>
                                <span className="text-sm text-gray-700">{units} units open</span>
                                <span className="text-sm text-gray-700">
                                    {union.length} distinct FCs <span className="text-emerald-700">(+{addedFcs} new)</span>
                                </span>
                                {/* Owner, 2026-08-26: "show the current PP before and after" —
                                    both figures are the server's projections (before = stocked
                                    FCs, after = stocked + selection), never a client-side sum
                                    of marginal gains. */}
                                {typeof data.basketGvs1Day === 'number' && (
                                    <span className="text-sm text-gray-700"
                                          title="Share of demand seeing a ≤1-day promise: current stock → with the selected plans confirmed">
                                        ≤1 day: {data.currentGvs1Day}% →{' '}
                                        <span className="font-semibold text-emerald-800">{data.basketGvs1Day}%</span>{' '}
                                        <span className="text-emerald-700">
                                            ({formatGain(Math.round((data.basketGvs1Day - data.currentGvs1Day) * 10) / 10)})
                                        </span>
                                    </span>
                                )}
                            </div>
                            <div className="mt-2">{union.map(fc => (
                                <FcChip key={fc} code={fc} isNew={!(data.currentFcs || []).includes(fc)} />
                            ))}</div>

                            <div className="mt-3 divide-y divide-emerald-200 border-t border-emerald-200">
                                {sectionRows.map(p => {
                                    const done = confirmedPlans[p.runId];
                                    const printed = !!printedIds[p.runId];
                                    return (
                                        <div key={p.runId} className="py-2">
                                            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                                                <div className="text-sm text-gray-800 flex items-center gap-2 min-w-0">
                                                    <span className="font-medium">run {p.runId}</span>
                                                    <span>{p.units} units</span>
                                                    <span className="text-gray-500">{(p.fcs || []).length} FCs</span>
                                                    {p.recommended && (
                                                        <span className="px-1 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-700 border border-emerald-300"
                                                              title="Scored TAKE — recommended to confirm">
                                                            TAKE
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 shrink-0">
                                                    {done ? (
                                                        <>
                                                            {/* One-way latch: a confirmed shipment cannot be
                                                                re-confirmed, so the button says so and stays dead. */}
                                                            <button disabled
                                                                    className="px-3 py-1 text-xs font-medium rounded-md bg-emerald-600 text-white opacity-70 cursor-default">
                                                                ✓ Confirmed
                                                                {done.shipmentCount != null
                                                                    ? ` — ${done.shipmentCount} shipment${done.shipmentCount !== 1 ? 's' : ''}`
                                                                    : ''}
                                                            </button>
                                                            {/* Printing stays available after use (reprints are
                                                                normal); it only changes colour to show it ran. */}
                                                            <span onClickCapture={() => setPrintedIds(prev => ({ ...prev, [p.runId]: true }))}>
                                                                <PrintLabelsAction
                                                                    runId={p.runId}
                                                                    planId={p.inboundPlanId}
                                                                    className={`flex items-center gap-1 text-xs hover:underline disabled:opacity-50 ${
                                                                        printed
                                                                            ? 'text-amber-600 hover:text-amber-700'
                                                                            : 'text-indigo-600 hover:text-indigo-800'}`}
                                                                />
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <button
                                                            onClick={() => confirmPlan(p)}
                                                            disabled={confirmingId != null || p.expired || !p.inboundPlanId || !p.placementOptionId}
                                                            title={p.expired
                                                                ? 'This draw is past Amazon\u2019s confirmability deadline — re-analyze to refresh it'
                                                                : 'Confirm this plan with Amazon and book partnered transport'}
                                                            className="px-3 py-1 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            {confirmingId === p.runId ? 'Confirming…' : 'Confirm shipment'}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            {confirmErrors[p.runId] && (
                                                <div className="mt-1 text-xs text-red-700">
                                                    {confirmErrors[p.runId]}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {selected.length > 0 && (
                                <div className="mt-2 flex items-center gap-4">
                                    <button onClick={() => { setSelected([]); snapshotPlans([], null); load([], true); }}
                                            className="text-xs text-gray-600 hover:text-gray-800 underline">
                                        clear selection
                                    </button>
                                    {/* Deselecting and removing are different acts: clear keeps the
                                        draws alive for later, remove cancels them AND stops the
                                        6-hourly watch so they stop coming back. */}
                                    <button onClick={removePlans}
                                            disabled={removing}
                                            title="Cancel the selected draws and stop the background watch for this ASIN"
                                            className="text-xs text-red-600 hover:text-red-800 underline disabled:opacity-50">
                                        {removing
                                            ? 'Removing…'
                                            : `Remove ${selected.length} plan${selected.length !== 1 ? 's' : ''} & stop draws`}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {live.length === 0 && (
                        <div className="text-sm text-gray-500 bg-gray-50 border rounded-md px-4 py-6 text-center">
                            No confirmable draws. Every open plan for this ASIN is past Amazon\u2019s confirmability deadline —
                            re-analyze to get fresh ones.
                        </div>
                    )}
                    <div className="space-y-2">
                        {live.map(g => {
                            const best = (g.variants || [])[0];
                            const picked = (g.variants || []).some(v => selected.includes(v.runId));
                            const newSet = new Set(g.newFcs || []);
                            return (
                                <div key={g.fcs.join('-')}
                                     className={`border rounded-md px-4 py-3 ${picked ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200'}`}>
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="min-w-0">
                                            <div className="mb-1">
                                                {g.recommended && (
                                                    <span className="inline-block px-1.5 py-0.5 mr-1 mb-1 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-700 border border-emerald-300"
                                                          title="Scored TAKE — recommended to confirm">
                                                        TAKE
                                                    </span>
                                                )}
                                                {(g.fcs || []).map(fc => (
                                                    <FcChip key={fc} code={fc} isNew={newSet.has(fc)} covered={!newSet.has(fc)} />
                                                ))}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {(g.newFcs || []).length} new FC{(g.newFcs || []).length !== 1 ? 's' : ''}
                                                {' · '}
                                                {(g.variants || []).length} size option{(g.variants || []).length !== 1 ? 's' : ''}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className={`text-lg font-semibold ${
                                                (g.marginalGain1Day || 0) > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>
                                                {formatGain(g.marginalGain1Day)}
                                            </div>
                                            <div className="text-xs text-gray-500">GVS ≤1 day</div>
                                        </div>
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {(g.variants || []).filter(v => !v.expired).map(v => (
                                            <button
                                                key={v.runId}
                                                onClick={() => toggle(v.runId)}
                                                className={`text-xs px-2 py-1 rounded border ${
                                                    selected.includes(v.runId)
                                                        ? 'bg-emerald-600 text-white border-emerald-600'
                                                        : 'bg-white border-gray-300 hover:bg-gray-50'}`}
                                                title={describeAge(v.ageHours, v.expired, v.confirmableUntil)}
                                            >
                                                {selected.includes(v.runId) ? '✓ ' : ''}{v.units} units
                                                <span className="opacity-70"> · run {v.runId}</span>
                                            </button>
                                        ))}
                                        <span className="text-xs text-gray-400 self-center">
                                            {best ? describeAge(best.ageHours, best.expired, best.confirmableUntil) : ''}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {expired.length > 0 && (
                        <div className="mt-5">
                            <button onClick={() => setShowExpired(v => !v)}
                                    className="text-sm text-gray-500 hover:text-gray-700 underline">
                                {showExpired ? 'Hide' : 'Show'} {expired.length} expired FC set
                                {expired.length !== 1 ? 's' : ''} — past Amazon\u2019s confirmability deadline, re-analyze to refresh
                            </button>
                            {showExpired && (
                                <div className="mt-2 space-y-1">
                                    {expired.map(g => (
                                        <div key={g.fcs.join('-')} className="text-xs text-gray-400 border-b py-1">
                                            {(g.fcs || []).join(', ')} · {(g.variants || []).length} draw
                                            {(g.variants || []).length !== 1 ? 's' : ''}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
